import { Button, Icon } from '@chakra-ui/react'
import { addHours } from 'date-fns'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { StorageKeys } from '~/atoms/StorageKeys'
import { Text } from '~/components/core'
import { Tooltip } from '~/components/ui/tooltip'
import { useChangelogModal } from '~/hooks/useChangelogModal'
import { useClientUpdateAvailableCheck } from '~/hooks/useCheckClientVersion'
import { usePartnerDesktopAppName } from '~/hooks/useCurrentPartner'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { BannerIconButton } from '~/pages/Home/Banners/BannerControls'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { observe } from '~/services/observability/observe'
import { AnalyticsNamespace, SharedAnalyticsEvents } from '~/types/Analytics'
import { isElectron, isLocalBuildEnvironment } from '~/utils/common'

interface WrapperProps {
  $show: boolean
}

const Wrapper = styled.div<WrapperProps>`
  height: ${({ $show }) => ($show ? '56' : '0')}px;
  padding: 0 16px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  background-color: var(--chakra-colors-green-solid);
  align-items: center;
  overflow: hidden;
  transition: height 200ms ease;
`
const TextWrapper = styled.div`
  display: flex;
  align-items: center;
`
interface AppVersionOutOfDateBannerState {
  lastDismissedDate?: Date | undefined
  lastDismissedVersion?: string | undefined
}

const versionOutOfDateBannerStateAtom =
  atomWithStorage<AppVersionOutOfDateBannerState>(
    StorageKeys.AppVersionBanner,
    {},
  )

const AppVersionOutOfDateBanner = ({ onShowCallback }: BannerProps) => {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  const { showChangelog, ChangelogModal } = useChangelogModal()
  const { lastChecked, currentVersion, latestVersion, isClientOutOfDate } =
    useClientUpdateAvailableCheck()
  const [versionOutOfDateBannerState, setVersionOutOfDateBannerState] = useAtom(
    versionOutOfDateBannerStateAtom,
  )
  const { availableVersion, isUpdateDownloaded, quitAndInstall } =
    useElectronUpdaterContext()
  const desktopAppName = usePartnerDesktopAppName()

  const {
    state: {
      phoneCalls: {
        list: { callList },
      },
    },
  } = usePhoneEndpoint({})

  const shouldShowBanner = useCallback((): boolean => {
    if (isElectron) {
      return isUpdateDownloaded
    } else {
      if (isLocalBuildEnvironment() || !isClientOutOfDate) return false
      if (!versionOutOfDateBannerState.lastDismissedDate) return true

      // Consider moving date parsing outside the check if possible
      const lastDismissedDate = new Date(
        versionOutOfDateBannerState.lastDismissedDate,
      )
      const lastDismissedVersion =
        versionOutOfDateBannerState.lastDismissedVersion

      return (
        addHours(lastDismissedDate, 1) < new Date() ||
        lastDismissedVersion !== latestVersion
      )
    }
  }, [
    isElectron,
    isClientOutOfDate,
    versionOutOfDateBannerState.lastDismissedDate,
    versionOutOfDateBannerState.lastDismissedVersion,
    latestVersion,
    isUpdateDownloaded,
  ])

  const updateVersionOutOfDateBannerState = useCallback(() => {
    setVersionOutOfDateBannerState({
      lastDismissedDate: new Date(),
      lastDismissedVersion: latestVersion,
    })
  }, [setVersionOutOfDateBannerState, latestVersion])

  const handleSetShow = useCallback(
    (val: boolean) => {
      setShow(val)
      if (!val) {
        // Only update dismissal state when hiding the banner
        updateVersionOutOfDateBannerState()
      }
      onShowCallback(val)
    },
    [updateVersionOutOfDateBannerState, onShowCallback],
  )

  const tempHideBanner = useMemo(() => {
    return callList.size > 0
  }, [callList])

  useEffect(() => {
    const shouldShow = shouldShowBanner()
    if (!isElectron && !show && shouldShow) {
      observe.track(
        AnalyticsNamespace.SHARED,
        SharedAnalyticsEvents.VersionBannerShown,
        { latestVersion, currentVersion },
      )

      // Use setTimeout to defer state update to next tick
      setTimeout(() => {
        handleSetShow(true)
      }, 0)
    }
  }, [
    show,
    lastChecked,
    isClientOutOfDate,
    latestVersion,
    currentVersion,
    shouldShowBanner,
    handleSetShow,
  ])

  useEffect(() => {
    if (!show) {
      return
    }
    const shouldShowNow = !tempHideBanner && show
    onShowCallback(shouldShowNow)
  }, [tempHideBanner, show, onShowCallback])

  // Desktop: show as soon as an update has downloaded. Gated only on
  // isUpdateDownloaded — the web isClientOutOfDate check doesn't apply here
  // (electron loads the remote bundle, so it's ~always false on desktop).
  useEffect(() => {
    if (isElectron && isUpdateDownloaded) {
      onShowCallback(true)
      setShow(true)
    }
  }, [isUpdateDownloaded, onShowCallback])

  const dismissBanner = useCallback(() => {
    observe.track(
      AnalyticsNamespace.SHARED,
      SharedAnalyticsEvents.VersionBannerDismissed,
      { latestVersion, currentVersion },
    )
    handleSetShow(false)
  }, [handleSetShow, latestVersion, currentVersion])

  const handleRefreshClicked = useCallback(() => {
    observe.track(
      AnalyticsNamespace.SHARED,
      SharedAnalyticsEvents.VersionBannerRefreshed,
      { latestVersion, currentVersion },
    )
    window.location.reload()
  }, [latestVersion, currentVersion])

  const willShowBanner =
    !tempHideBanner &&
    ((!isElectron && show) || (isElectron && isUpdateDownloaded && show))

  return (
    <Wrapper $show={willShowBanner}>
      <TextWrapper>
        <Text color="var(--chakra-colors-brand-contrast)">
          {isElectron
            ? t('appVersionBanner.desktopUpdateAvailable', {
                version: availableVersion,
                appName: desktopAppName,
              })
            : t('appVersionBanner.webUpdateAvailable')}
        </Text>
        <Button
          marginLeft={'.5rem'}
          onClick={isElectron ? quitAndInstall : handleRefreshClicked}
          bg="bg.panel"
          color="fg.default"
          borderRadius="full"
          _hover={{ bg: 'bg.muted' }}
          size="sm"
        >
          {isElectron
            ? t('appVersionBanner.restartAndUpdate')
            : t('appVersionBanner.updateNow')}
        </Button>
        {isElectron && (
          <Button
            marginLeft=".5rem"
            onClick={showChangelog}
            bg="transparent"
            color="var(--chakra-colors-on-primary)"
            textDecoration="underline"
            size="sm"
            variant="ghost"
          >
            {t('appVersionBanner.whatsNew')}
          </Button>
        )}
      </TextWrapper>
      <Tooltip
        content={t('appVersionBanner.remindLater', 'Remind me later')}
        positioning={{ placement: 'left' }}
      >
        <BannerIconButton
          aria-label={t('appVersionBanner.remindLater', 'Remind me later')}
          marginLeft="0.5rem"
          onClick={() => dismissBanner()}
        >
          <Icon boxSize="20px" color="brand.contrast" aria-hidden>
            <X size="100%" />
          </Icon>
        </BannerIconButton>
      </Tooltip>
      {ChangelogModal}
    </Wrapper>
  )
}

export default AppVersionOutOfDateBanner
